import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AppUrls } from './app-urls';
import { LandingPage } from '../landing/landing.page';
import { ShellLayout } from '../shell/shell.layout';
import { authGuard } from '../core/authentication/auth.guard';
import { privilegeGuard } from '../core/authentication/privilege.guard';
import { juyoAdminGuard } from '../core/authentication/juyo-admin.guard';
import { dataDropAccessGuard, hotelGptAccessGuard } from '../core/authentication/feature-access.guard';
import { ApplicationStore } from '../core/store/application.store';
import { NoAccessPage } from '../no-access/no-access.page';
import { OrganizationPrivilege, UserPrivilege } from '../shared/types/organization';

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: AppUrls.signInUrl,
    },
    {
        path: AppUrls.signInUrl,
        component: LandingPage,
    },
    {
        path: AppUrls.registrationUrl,
        loadComponent: () => import('../reset-password/registration.page').then((m) => m.RegistrationPage),
    },
    {
        path: AppUrls.resetPasswordUrl,
        loadComponent: () => import('../reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
    },
    {
        path: AppUrls.newPasswordUrl,
        loadComponent: () => import('../reset-password/new-password.page').then((m) => m.NewPasswordPage),
    },
    {
        path: AppUrls.onboardingUrl,
        loadComponent: () => import('../onboarding/onboarding.page').then((m) => m.OnboardingPage),
    },
    {
        path: AppUrls.noAccessUrl,
        component: NoAccessPage,
        canActivate: [authGuard],
    },
    {
        path: '',
        component: ShellLayout,
        canActivate: [authGuard, privilegeGuard(OrganizationPrivilege.OrgAccess), hotelGptAccessGuard],
        // Re-run the guards when the org changes (orgId query param) so a user who switches to an
        // organization without access is redirected to the no-access page.
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        children: [
            {
                path: AppUrls.dataDropUrl,
                canActivate: [dataDropAccessGuard],
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () => import('../data-drop/data-drop.page').then((m) => m.DataDropPage),
            },
            {
                path: AppUrls.hotelGptUrl,
                loadComponent: () => import('../hotel-gpt/hotel-gpt.page').then((m) => m.HotelGptPage),
            },
            {
                path: AppUrls.organizationSettingsUrl,
                canActivate: [
                    privilegeGuard([
                        OrganizationPrivilege.OrgManageAllUsersAndProps,
                        OrganizationPrivilege.OrgManageUsersAndProps,
                        OrganizationPrivilege.OrgManageOwnUsersAndProps,
                    ]),
                ],
                // Re-run the privilege guard when the org changes (orgId query param) so a user who
                // switches to an organization without this privilege is redirected away from the page.
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('../settings/organization-settings/organization-settings.page').then(
                                (m) => m.OrganizationSettingsPage,
                            ),
                    },
                    {
                        path: AppUrls.organizationSettingsPropertiesPath,
                        loadComponent: () =>
                            import('../settings/properties/properties.page').then((m) => m.PropertiesPage),
                    },
                    {
                        path: AppUrls.organizationSettingsUsersPath,
                        loadComponent: () =>
                            import('../settings/users-access/users-access.page').then((m) => m.UsersAccessPage),
                    },
                    {
                        path: AppUrls.organizationSettingsAccessRequestsPath,
                        loadComponent: () =>
                            import('../settings/access-requests/access-requests.page').then(
                                (m) => m.AccessRequestsPage,
                            ),
                    },
                    {
                        path: AppUrls.organizationSettingsUsagePath,
                        canActivate: [privilegeGuard(OrganizationPrivilege.OrgUsageStats)],
                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                        loadComponent: () => import('../settings/usage/usage.page').then((m) => m.UsagePage),
                    },
                    // {
                    //     path: AppUrls.organizationSettingsBillingPath,
                    //     loadComponent: () =>
                    //         import('../settings/billing/billing.page').then(
                    //             (m) => m.BillingPage,
                    //         ),
                    // },
                ],
            },
            {
                path: AppUrls.adminUrl,
                canActivate: [juyoAdminGuard],
                // Re-run the guard when the org changes (orgId query param) so a user who
                // switches to an organization where they are not a Juyo admin is redirected away.
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: () => {
                            const applicationStore = inject(ApplicationStore);
                            if (applicationStore.hasPrivilege(UserPrivilege.ManageBroadcast)) {
                                return AppUrls.adminBroadcastPath;
                            }
                            if (
                                applicationStore.hasPrivilege([
                                    UserPrivilege.FullAccessUsageConfig,
                                    UserPrivilege.ReadOnlyUsageConfig,
                                ])
                            ) {
                                return AppUrls.adminUsageRatesPath;
                            }
                            return inject(Router).createUrlTree(['/', AppUrls.hotelGptUrl]);
                        },
                    },
                    {
                        path: AppUrls.adminBroadcastPath,
                        canActivate: [privilegeGuard(UserPrivilege.ManageBroadcast)],
                        loadComponent: () => import('../admin/broadcast/broadcast.page').then((m) => m.BroadcastPage),
                    },
                    {
                        path: AppUrls.adminUsageRatesPath,
                        canActivate: [
                            privilegeGuard([UserPrivilege.FullAccessUsageConfig, UserPrivilege.ReadOnlyUsageConfig]),
                        ],
                        loadComponent: () =>
                            import('../admin/usage-rates/usage-rates.page').then((m) => m.UsageRatesPage),
                    },
                ],
            },
            {
                path: AppUrls.accountSettingsUrl,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: AppUrls.accountSettingsProfilePath,
                    },
                    {
                        path: AppUrls.accountSettingsProfilePath,
                        loadComponent: () =>
                            import('../settings/account-settings/profile.page').then((m) => m.ProfilePage),
                    },
                    {
                        path: AppUrls.accountSettingsMyAccessPath,
                        redirectTo: () =>
                            inject(Router).createUrlTree(
                                ['/', AppUrls.accountSettingsUrl, AppUrls.accountSettingsProfilePath],
                                { queryParams: { tab: 'access' } },
                            ),
                    },
                ],
            },
        ],
    },
    {
        path: '**',
        redirectTo: AppUrls.signInUrl,
    },
];
